import { Play } from '@tunasong/icons'
import { METER_POOL } from '@tunasong/music-lib'
import type { MeterKind, MeterQuestion } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { RhythmAnswerButton } from './answer-button.js'
import type { RhythmAnswerButtonState } from './answer-button.js'
import styles from './meter-question.module.css'

export interface MeterQuestionPanelProps {
  question: MeterQuestion
  scoreCorrect: number
  scoreAnswered: number
  /**
   * When false, answer buttons render disabled (post-answer feedback phase).
   * The Hear-it button stays interactive in both modes.
   */
  interactive: boolean
  onHearIt: () => void
  onAnswer: (picked: MeterKind) => void
}

/**
 * Rhythm Trainer Meter ID question panel. Renders the generic "What meter
 * did you hear?" prompt + Hear-it audition button + 5-button answer grid
 * (4/4, 3/4, 6/8, 5/4, 7/8 from METER_POOL). Stateless — the host owns
 * session state.
 *
 * Unlike Function Quiz, the panel deliberately does NOT render the meter
 * name in the header: the structural name IS the answer.
 *
 * Spec: docs/superpowers/specs/2026-05-22-rhythm-trainer-v1-design.md §6.2
 */
export const MeterQuestionPanel: FC<MeterQuestionPanelProps> = props => {
  const { scoreCorrect, scoreAnswered, interactive, onHearIt, onAnswer } = props
  const buttonState: RhythmAnswerButtonState = interactive ? 'idle' : 'dimmed'
  return (
    <section className={styles.question} aria-label="Rhythm Trainer meter question">
      <header className={styles.header}>
        <span
          className={styles.scoreChip}
          aria-label={`${scoreCorrect} correct out of ${scoreAnswered} answered`}
        >
          {scoreCorrect} / {scoreAnswered}
        </span>
      </header>
      <p className={styles.prompt}>What meter did you hear?</p>
      <div className={styles.actionRow}>
        <Button variant="outlined" size="small" startIcon={<Play />} onClick={onHearIt}>
          Hear it
        </Button>
      </div>
      <div className={styles.cardGrid}>
        {METER_POOL.map(entry => (
          <RhythmAnswerButton
            key={entry.kind}
            pickValue={entry.kind}
            lexiconId={entry.lexiconId}
            fallbackLongName={entry.displayName}
            fallbackShortName={entry.displayNameShort}
            state={buttonState}
            onPick={picked => onAnswer(picked as MeterKind)}
          />
        ))}
      </div>
    </section>
  )
}
